import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 2 — recruiter action modal.
 *
 * Two modes:
 *   variant="interview" → recruiter fills recruiter_name, company, email, and a free-form message.
 *   variant="followups" → recruiter fills recruiter_name, company, email, and 1-3 follow-up questions.
 *
 * Anti-bot:
 *   - hidden honeypot input named `hp_company_url` (off-screen, not visible to humans)
 *   - server enforces real rate limiting; we just show a friendly toast for 429s
 *
 * Submits to /api/public/recruiter/{userId}/request-interview or /follow-ups.
 */
export default function RecruiterActionModal({
  open,
  variant,
  userId,
  candidateName,
  onClose,
}) {
  const initialQs = ["", "", ""];
  const [recruiterName, setRecruiterName] = useState("");
  const [recruiterCompany, setRecruiterCompany] = useState("");
  const [recruiterEmail, setRecruiterEmail] = useState("");
  const [message, setMessage] = useState("");
  const [questions, setQuestions] = useState(initialQs);
  const [hpCompanyUrl, setHpCompanyUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState("idle"); // idle | success | error
  const [submitError, setSubmitError] = useState("");
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (open) {
      setRecruiterName("");
      setRecruiterCompany("");
      setRecruiterEmail("");
      setMessage("");
      setQuestions(initialQs);
      setHpCompanyUrl("");
      setSubmitState("idle");
      setSubmitError("");
      // Focus first field after mount
      setTimeout(() => firstFieldRef.current?.focus(), 30);
    }
  }, [open]);

  if (!open) return null;

  const isInterview = variant === "interview";
  const filledQuestions = questions.map((q) => q.trim()).filter((q) => q.length > 0);
  const minQuestions = isInterview ? 0 : 1;
  const isValid =
    recruiterName.trim().length >= 2 &&
    recruiterCompany.trim().length >= 1 &&
    /\S+@\S+\.\S+/.test(recruiterEmail) &&
    (isInterview ? message.trim().length > 0 : filledQuestions.length >= minQuestions);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const path = isInterview ? "request-interview" : "follow-ups";
      const body = {
        recruiter_name: recruiterName.trim(),
        recruiter_company: recruiterCompany.trim(),
        recruiter_email: recruiterEmail.trim(),
        hp_company_url: hpCompanyUrl,
      };
      if (isInterview) body.message = message.trim();
      else body.questions = filledQuestions;
      const res = await fetch(`${API}/api/public/recruiter/${userId}/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail =
          (await res.json().catch(() => ({}))).detail ||
          (res.status === 429
            ? "You've already sent this candidate a message recently — please try again later."
            : "Could not send. Please try again.");
        throw new Error(detail);
      }
      setSubmitState("success");
    } catch (err) {
      setSubmitError(String(err.message || err));
      setSubmitState("error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      data-testid="recruiter-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] font-semibold text-blue-600">
              {isInterview ? "Request Interview" : "Follow-Up Questions"}
            </div>
            <h2 className="mt-0.5 text-lg font-bold text-slate-900">
              {candidateName ? `Reach out to ${candidateName}` : "Reach the candidate"}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Inter-View routes your message — the candidate&rsquo;s personal contact info stays private.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close"
            data-testid="recruiter-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {submitState === "success" ? (
          <div className="p-8 text-center" data-testid="recruiter-modal-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h3 className="mt-3 text-lg font-bold text-slate-900">Message sent</h3>
            <p className="mt-1.5 text-sm text-slate-600">
              {candidateName || "The candidate"} has been notified. They&rsquo;ll see your note in their inbox and can reply directly to you at{" "}
              <span className="font-semibold text-slate-900">{recruiterEmail}</span>.
            </p>
            <Button
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={onClose}
              data-testid="recruiter-modal-success-close"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="recruiter-modal-form">
            {/* Honeypot — hidden from real users with off-screen positioning + aria-hidden */}
            <div
              aria-hidden="true"
              tabIndex={-1}
              className="absolute pointer-events-none"
              style={{ left: "-9999px", top: "-9999px", height: 0, width: 0, opacity: 0 }}
            >
              <label>
                Company URL
                <input
                  type="text"
                  name="hp_company_url"
                  value={hpCompanyUrl}
                  onChange={(e) => setHpCompanyUrl(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="rn" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Your name
                </Label>
                <Input
                  id="rn"
                  ref={firstFieldRef}
                  value={recruiterName}
                  onChange={(e) => setRecruiterName(e.target.value)}
                  placeholder="Jane Recruiter"
                  maxLength={80}
                  required
                  data-testid="recruiter-modal-name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="rc" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Company
                </Label>
                <Input
                  id="rc"
                  value={recruiterCompany}
                  onChange={(e) => setRecruiterCompany(e.target.value)}
                  placeholder="Acme Hiring Co."
                  maxLength={80}
                  required
                  data-testid="recruiter-modal-company"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="re" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                Reply-to email
              </Label>
              <Input
                id="re"
                type="email"
                value={recruiterEmail}
                onChange={(e) => setRecruiterEmail(e.target.value)}
                placeholder="you@company.com"
                maxLength={200}
                required
                data-testid="recruiter-modal-email"
                className="mt-1"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                The candidate replies directly to this address.
              </p>
            </div>

            {isInterview ? (
              <div>
                <Label htmlFor="rm" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Your message
                </Label>
                <Textarea
                  id="rm"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  maxLength={2000}
                  placeholder="Briefly introduce yourself and the role, and propose 2-3 time windows that work for you."
                  required
                  data-testid="recruiter-modal-message"
                  className="mt-1"
                />
                <p className="mt-1 text-[11px] text-slate-500 text-right">{message.length}/2000</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Questions (up to 3)
                </Label>
                {questions.map((q, i) => (
                  <Input
                    key={i}
                    value={q}
                    onChange={(e) => {
                      const next = [...questions];
                      next[i] = e.target.value;
                      setQuestions(next);
                    }}
                    maxLength={500}
                    placeholder={
                      i === 0
                        ? "What is your earliest available start date?"
                        : i === 1
                        ? "(optional) Which days of the week can you work?"
                        : "(optional) Are you certified for this role?"
                    }
                    data-testid={`recruiter-modal-question-${i}`}
                  />
                ))}
                <p className="text-[11px] text-slate-500">
                  At least one question required. The candidate will see them as a numbered list.
                </p>
              </div>
            )}

            {submitState === "error" && (
              <div
                className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-md px-3 py-2"
                data-testid="recruiter-modal-error"
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{submitError}</span>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-slate-300 text-slate-700"
                data-testid="recruiter-modal-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isValid || submitting}
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-600/40 disabled:cursor-not-allowed"
                data-testid="recruiter-modal-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : isInterview ? (
                  "Send interview request"
                ) : (
                  `Send ${filledQuestions.length || ""} question${filledQuestions.length === 1 ? "" : "s"}`.trim()
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
